import { useEffect, useCallback } from "react";

import {
    ORIGINAL_REFERRER_PARAM,
    UTM_PARAMS,
    type TAttribution,
} from "~/database/types";

const ATTRIBUTION_STORAGE_KEY = "utm_attribution";

/**
 * Sanitize referrer to only include origin (no path or query params)
 * AIDEV-NOTE: Security measure - referrer URLs can contain sensitive data in query params
 */
function sanitizeReferrer(referrer: string): string | undefined {
    if (!referrer) return undefined;

    try {
        const url = new URL(referrer);
        return url.origin; // Just protocol + host, no path or params
    } catch {
        return undefined; // Invalid URL
    }
}

// AIDEV-NOTE: Ad platform click IDs captured alongside UTMs for server-side conversion tracking.
// fbclid = Meta/Facebook, gclid = Google Ads, rdt_cid = Reddit Ads. Stored in attribution JSONB, no migration needed.
const CLICK_ID_PARAMS = ["fbclid", "gclid", "rdt_cid"] as const;

/**
 * Extract UTM parameters and ad platform click IDs from URL search params
 */
function extractUTMsFromURL(): Partial<TAttribution> {
    if (typeof window === "undefined") return {};

    const searchParams = new URLSearchParams(window.location.search);
    const utms: Partial<TAttribution> = {};

    for (const param of UTM_PARAMS) {
        const value = searchParams.get(param);
        if (value) {
            utms[param] = value;
        }
    }

    for (const param of CLICK_ID_PARAMS) {
        const value = searchParams.get(param);
        if (value) {
            utms[param] = value;
        }
    }

    return utms;
}

/** Forwarded pre-marketing-site referrer (?orig_ref=), reduced to origin like `referrer`. */
function extractOriginalReferrerFromURL(): string | undefined {
    if (typeof window === "undefined") return undefined;
    const value = new URLSearchParams(window.location.search).get(
        ORIGINAL_REFERRER_PARAM,
    );
    return value ? sanitizeReferrer(value) : undefined;
}

/**
 * Get stored attribution from sessionStorage
 * AIDEV-NOTE: Exported for use-attribution-flush (post-OAuth attribution write).
 */
export function getStoredAttribution(): TAttribution | null {
    if (typeof window === "undefined") return null;

    try {
        const stored = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch {
        return null;
    }
}

/**
 * Save attribution to sessionStorage
 */
function saveAttribution(attribution: TAttribution): void {
    if (typeof window === "undefined") return;

    try {
        sessionStorage.setItem(
            ATTRIBUTION_STORAGE_KEY,
            JSON.stringify(attribution),
        );
    } catch {
        // sessionStorage might be disabled or full
    }
}

/**
 * Hook to capture and persist UTM attribution data.
 *
 * AIDEV-NOTE: Used on the pages cold traffic lands on: landing, login, signup,
 * and the public session-sharing page. /with/:slug is deliberately absent — the
 * affiliate flow does its own server-side attribution (captureClick + cookie).
 * It captures UTM params and ad platform click IDs from URL on first arrival and persists them in sessionStorage.
 * The attribution data includes:
 * - UTM params (utm_campaign, utm_content, utm_source, utm_medium, utm_term)
 * - Ad click IDs (fbclid, gclid) for server-side conversion tracking
 * - document.referrer (where they came from)
 * - original_referrer (?orig_ref= from the marketing site: where they were before it)
 * - landing_page (first URL path they arrived at)
 * - captured_at (timestamp)
 *
 * Data persists in sessionStorage across page navigations until tab is closed.
 *
 * @returns getAttribution - function to retrieve current attribution data for form submission
 */
export function useUTMCapture() {
    useEffect(() => {
        if (typeof window === "undefined") return;

        const urlUTMs = extractUTMsFromURL();
        const originalReferrer = extractOriginalReferrerFromURL();
        const existingAttribution = getStoredAttribution();

        // Only capture if we have UTMs in URL OR we don't have any stored attribution yet
        const hasURLUTMs = Object.keys(urlUTMs).length > 0;
        const hasExistingAttribution = existingAttribution !== null;

        if (hasURLUTMs) {
            // URL has UTMs - always update with fresh data (user may have clicked new campaign link)
            const attribution: TAttribution = {
                ...urlUTMs,
                referrer: sanitizeReferrer(document.referrer),
                // First-touch: a later campaign click without orig_ref keeps the stored one.
                original_referrer:
                    originalReferrer ?? existingAttribution?.original_referrer,
                landing_page: window.location.pathname,
                captured_at: new Date().toISOString(),
            };
            saveAttribution(attribution);
        } else if (!hasExistingAttribution) {
            // No UTMs in URL and no existing attribution - capture referrer and landing page
            // This handles organic traffic without UTMs
            const attribution: TAttribution = {
                referrer: sanitizeReferrer(document.referrer),
                original_referrer: originalReferrer,
                landing_page: window.location.pathname,
                captured_at: new Date().toISOString(),
            };
            saveAttribution(attribution);
        } else if (originalReferrer && !existingAttribution.original_referrer) {
            // AIDEV-NOTE: An untagged arrival never replaces stored attribution, but it can fill
            // in the forwarded original referrer the first capture lacked.
            saveAttribution({
                ...existingAttribution,
                original_referrer: originalReferrer,
            });
        }
        // If no UTMs in URL but we have existing attribution, keep the existing data
    }, []);

    /**
     * Get current attribution data for form submission
     * Returns null if no attribution has been captured
     */
    const getAttribution = useCallback((): TAttribution | null => {
        return getStoredAttribution();
    }, []);

    return { getAttribution };
}

/**
 * Clear stored attribution (useful after successful signup)
 */
export function clearStoredAttribution(): void {
    if (typeof window === "undefined") return;

    try {
        sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
    } catch {
        // sessionStorage might be disabled
    }
}
